/**
 * Markdown view of a document saved by the (now hidden) BlockNote editor.
 *
 * `content` (markdown) is the copy every writer updates: the old editor, the API, the CLI and MCP. `contentJson`
 * is only kept current by the old editor, so it is converted only when no markdown was ever written. Formatting
 * markdown cannot carry (colors, underline, alignment, indentation, media, image size, table column widths) is
 * detected in the JSON so such documents stay read-only until the user explicitly converts them.
 */

import { hasText } from './editor-content-fallback'

export type LossyFeature = 'color' | 'underline' | 'alignment' | 'indentation' | 'media' | 'imageSize' | 'tableLayout' | 'unknownBlock'

/** Block types whose markdown form keeps their meaning */
const MARKDOWN_SAFE_BLOCKS = new Set([
  'paragraph',
  'heading',
  'bulletListItem',
  'numberedListItem',
  'checkListItem',
  'codeBlock',
  'blockquote',
  'table',
  'image',
])

const LIST_BLOCKS = new Set(['bulletListItem', 'numberedListItem', 'checkListItem'])

const MEDIA_BLOCKS = new Set(['video', 'audio', 'file'])

type Json = Record<string, unknown>

const isRecord = (value: unknown): value is Json => typeof value === 'object' && value !== null && !Array.isArray(value)

const isColored = (value: unknown) => typeof value === 'string' && value !== '' && value !== 'default'

/** Inline styles and cell/block props anywhere below `node` */
function collectInline(node: unknown, found: Set<LossyFeature>): void {
  if (Array.isArray(node)) {
    node.forEach((child) => collectInline(child, found))
    return
  }
  if (!isRecord(node)) return
  const styles = node.styles
  if (isRecord(styles)) {
    if (styles.underline) found.add('underline')
    if (isColored(styles.textColor) || isColored(styles.backgroundColor)) found.add('color')
  }
  collectProps(node.props, found)
  for (const key of ['content', 'rows', 'cells']) collectInline(node[key], found)
}

function collectProps(props: unknown, found: Set<LossyFeature>): void {
  if (!isRecord(props)) return
  if (isColored(props.textColor) || isColored(props.backgroundColor)) found.add('color')
  if (typeof props.textAlignment === 'string' && props.textAlignment !== 'left') found.add('alignment')
}

function collectBlocks(blocks: unknown, parentType: string | null, found: Set<LossyFeature>): void {
  if (!Array.isArray(blocks)) return
  for (const block of blocks) {
    if (!isRecord(block)) continue
    const type = typeof block.type === 'string' ? block.type : ''
    if (MEDIA_BLOCKS.has(type)) found.add('media')
    else if (!MARKDOWN_SAFE_BLOCKS.has(type)) found.add('unknownBlock')
    // Nested blocks only have a markdown form under list items
    if (parentType !== null && !(LIST_BLOCKS.has(parentType) && LIST_BLOCKS.has(type))) found.add('indentation')
    collectProps(block.props, found)
    if (type === 'image' && isRecord(block.props) && block.props.previewWidth !== undefined && block.props.previewWidth !== 512) {
      found.add('imageSize')
    }
    if (isRecord(block.content) && Array.isArray(block.content.columnWidths) && block.content.columnWidths.some((w) => typeof w === 'number')) {
      found.add('tableLayout')
    }
    collectInline(block.content, found)
    collectBlocks(block.children, type, found)
  }
}

/** Formatting in BlockNote JSON that converting to markdown would drop (empty when conversion is safe) */
export function findLossyFormatting(contentJson: unknown): LossyFeature[] {
  const found = new Set<LossyFeature>()
  collectBlocks(contentJson, null, found)
  return [...found]
}

/** True when the document carries BlockNote JSON with text, i.e. the editor state is the newest copy */
export function hasEditorContent(contentJson: unknown): boolean {
  return Array.isArray(contentJson) && contentJson.some(hasText)
}

/** Convert BlockNote JSON to markdown headlessly with the same schema as the old editor (lazy-loaded) */
async function createConverter() {
  const [{ BlockNoteEditor }, { editorSchema }] = await Promise.all([import('@blocknote/core'), import('../components/editor/editor-schema')])
  return BlockNoteEditor.create({ schema: editorSchema })
}

/** One headless editor is enough: conversion does not mutate it */
let converter: ReturnType<typeof createConverter> | null = null

export async function contentJsonToMarkdown(contentJson: unknown): Promise<string> {
  // A failed load (e.g. chunk fetch error) is not cached, so the next call retries
  converter ??= createConverter().catch((err) => {
    converter = null
    throw err
  })
  const editor = await converter
  return editor.blocksToMarkdownLossy(contentJson as Parameters<typeof editor.blocksToMarkdownLossy>[0])
}
